import { authHeaders, clearSession, setSession } from "./auth";
import { API_BASE_URL } from "./config";
import type {
  Direction,
  InvestorTrendResponse,
  LoginResponse,
  Market,
  MeResponse,
  RsiHistoryResponse,
  SignalHistoryResponse,
  SignalListResponse,
  SimpleResponse,
  Ticker,
  TickerListResponse,
} from "./types";

export function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError";
}

/**
 * HTTP 상태를 그대로 노출하는 에러.
 * 호출부가 "없음"(404)과 실제 장애를 구분해야 하는 경우가 있다
 * — 예: 아직 신호가 산출되지 않은 종목의 신호 이력은 404 지만 오류가 아니다.
 * `status: 0` 은 응답 자체를 받지 못한 경우(네트워크 단절/CORS 차단).
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** FastAPI 의 `{detail: ...}` 를 우선 사용해 읽을 수 있는 에러 메시지를 만든다. */
async function toError(res: Response, path: string): Promise<ApiError> {
  let detail = "";
  try {
    const body = (await res.json()) as { detail?: unknown };
    if (typeof body.detail === "string") detail = body.detail;
  } catch {
    // 본문이 JSON 이 아니면 상태 코드만 쓴다.
  }
  return new ApiError(detail || `요청 실패 (${res.status}): ${path}`, res.status);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: { Accept: "application/json", ...init?.headers },
    });
  } catch (e) {
    if (isAbort(e)) throw e;
    // fetch 자체가 실패 = 네트워크 단절 / 잘못된 베이스 URL / CORS 차단
    throw new ApiError(
      "서버에 연결할 수 없습니다. 백엔드 주소(VITE_API_BASE_URL)와 네트워크를 확인하세요.",
      0,
    );
  }
  if (!res.ok) throw await toError(res, path);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { signal });
}

/**
 * 관리자 전용 요청. 토큰을 실어 보내고, 401 이면 저장된 세션을 버린다.
 *
 * 세션을 즉시 버려야 화면이 로그인 상태로 남아 계속 실패하지 않는다
 * (`useSession` 구독자들이 리렌더되며 라우트 가드가 로그인 화면으로 보낸다).
 */
async function adminRequest<T>(path: string, init: RequestInit): Promise<T> {
  try {
    return await request<T>(path, {
      ...init,
      headers: { ...init.headers, ...authHeaders() },
    });
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) clearSession();
    throw e;
  }
}

export const api = {
  listSignals(
    direction?: Direction,
    signal?: AbortSignal,
  ): Promise<SignalListResponse> {
    const q = direction ? `?direction=${direction}` : "";
    return getJson<SignalListResponse>(`/api/signals${q}`, signal);
  },

  signalHistory(
    symbol: string,
    limit = 50,
    signal?: AbortSignal,
  ): Promise<SignalHistoryResponse> {
    return getJson<SignalHistoryResponse>(
      `/api/signals/${encodeURIComponent(symbol)}/history?limit=${limit}`,
      signal,
    );
  },

  rsiHistory(
    symbol: string,
    days = 90,
    signal?: AbortSignal,
  ): Promise<RsiHistoryResponse> {
    return getJson<RsiHistoryResponse>(
      `/api/tickers/${encodeURIComponent(symbol)}/rsi-history?days=${days}`,
      signal,
    );
  },

  investorTrend(
    symbol: string,
    days = 30,
    signal?: AbortSignal,
  ): Promise<InvestorTrendResponse> {
    return getJson<InvestorTrendResponse>(
      `/api/tickers/${encodeURIComponent(symbol)}/investor-trend?days=${days}`,
      signal,
    );
  },

  listTickers(market?: Market, signal?: AbortSignal): Promise<TickerListResponse> {
    const q = market ? `?market=${market}` : "";
    return getJson<TickerListResponse>(`/api/tickers${q}`, signal);
  },

  /** 관리자 로그인. 성공하면 세션을 저장해 이후 관리자 요청에 자동으로 실린다. */
  async login(username: string, password: string): Promise<LoginResponse> {
    const res = await request<LoginResponse>("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    setSession({
      token: res.token,
      expiresAt: res.expires_at,
      username: res.username,
    });
    return res;
  },

  /** 저장된 토큰이 아직 유효한지 서버에 확인한다(비밀번호 변경·만료 감지). */
  me(signal?: AbortSignal): Promise<MeResponse> {
    return adminRequest<MeResponse>("/api/auth/me", { method: "GET", signal });
  },

  logout(): void {
    clearSession();
  },

  /**
   * 서버가 KIS 에서 시세를 다시 받아 스냅샷·신호를 재계산하게 한다(관리자 전용).
   *
   * 화면 재조회(`useFetch` 의 refresh)와 다르다 — 그쪽은 서버에 이미 있는 값을 다시
   * 읽을 뿐이다. 종목 수 × 2회의 외부 호출이라 수 초에서 십수 초가 걸린다.
   */
  refreshQuotes(): Promise<SimpleResponse> {
    return adminRequest<SimpleResponse>("/api/refresh", { method: "POST" });
  },

  registerTicker(input: {
    symbol: string;
    name: string;
    market: Market;
    excd?: string | null;
  }): Promise<Ticker> {
    return adminRequest<Ticker>("/api/tickers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  },

  deleteTicker(symbol: string): Promise<void> {
    return adminRequest<void>(`/api/tickers/${encodeURIComponent(symbol)}`, {
      method: "DELETE",
    });
  },

  registerDevice(token: string): Promise<void> {
    return request<void>("/api/devices", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
  },
};
